import {inject, NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';
import {CoreContainerComponent} from './components/core-container/core-container.component';
import {AuthGuardService} from '@appGuards/auth/auth-guard.service';
import {GeneralRouteGuard} from '@appGuards/core/general-route.guard';
import {appRoutes, ROUTE_TITLES} from '@appHelpers/core/app-routes';
import {TranslocoService} from '@ngneat/transloco';
import {TranslocoTitleStrategy} from '@app/strategies/transloco-title-strategy.service';

const routes: Routes = [
  {
    path: '',
    redirectTo: appRoutes.login,
    pathMatch: 'full',
    title: ROUTE_TITLES.ROOT,
  },
  {
    path: 'designSystem',
    loadChildren: () =>
      import('./components/design-system/design-system.module').then((m) => m.DesignSystemModule),
    title: ROUTE_TITLES.DESIGN_SYSTEM,
  },
  {
    path: appRoutes.login,
    loadChildren: () => import('./components/auth/login/login.module').then((m) => m.LoginModule),
    title: ROUTE_TITLES.LOGIN,
  },
  {
    path: appRoutes.testApi,
    loadChildren: () =>
      import('./components/test-api/test-api.module').then((m) => m.TestApiModule),
    title: ROUTE_TITLES.TEST_API,
  },
  {
    path: appRoutes.forgotPassword,
    loadChildren: () =>
      import('./components/auth/forgot-password/forgot-password.module').then(
        (m) => m.ForgotPasswordModule,
      ),
    title: ROUTE_TITLES.FORGOT_PASSWORD,
  },
  {
    path: appRoutes.protected,
    component: CoreContainerComponent,
    canActivate: [AuthGuardService],
    title: ROUTE_TITLES.PROTECTED,
    children: [
      {
        path: '',
        redirectTo: appRoutes.pendings.pendings,
        pathMatch: 'full',
        title: ROUTE_TITLES.PENDINGS,
      },
      {
        path: appRoutes.pendings.pendings,
        loadChildren: () =>
          import('@appComponents/pendings/pending.module').then((m) => m.PendingModule),
        canLoad: [GeneralRouteGuard],
        canActivate: [GeneralRouteGuard],
        title: ROUTE_TITLES.PENDINGS,
      },
      {
        path: appRoutes.improvements.improvements,
        loadChildren: () =>
          import('./components/under-construction/under-construction.module').then(
            (m) => m.UnderConstructionModule,
          ),
        canLoad: [GeneralRouteGuard],
        canActivate: [GeneralRouteGuard],
        title: ROUTE_TITLES.IMPROVEMENTS,
      },
      {
        path: appRoutes.incident.incident,
        loadChildren: () =>
          import('./components/under-construction/under-construction.module').then(
            (m) => m.UnderConstructionModule,
          ),
        canLoad: [GeneralRouteGuard],
        canActivate: [GeneralRouteGuard],
        title: ROUTE_TITLES.INCIDENT,
      },
      {
        path: appRoutes.request.request,
        loadChildren: () =>
          import('./components/under-construction/under-construction.module').then(
            (m) => m.UnderConstructionModule,
          ),
        canLoad: [GeneralRouteGuard],
        canActivate: [GeneralRouteGuard],
        title: ROUTE_TITLES.REQUEST,
      },
      {
        path: appRoutes.management.management,
        loadChildren: () =>
          import('./components/under-construction/under-construction.module').then(
            (m) => m.UnderConstructionModule,
          ),
        canLoad: [GeneralRouteGuard],
        canActivate: [GeneralRouteGuard],
        title: ROUTE_TITLES.MANAGEMENT,
      },
      {
        path: appRoutes.catalogs.catalogs,
        loadChildren: () =>
          import('./components/catalogos/catalogos-home/catalogos-home.module').then(
            (m) => m.CatalogosHomeModule,
          ),
        canLoad: [GeneralRouteGuard],
        canActivate: [GeneralRouteGuard],
        title: ROUTE_TITLES.CATALOGS,
      },
      {
        path: `${appRoutes.catalogs.catalogs}/${appRoutes.catalogs.clients.clients}`,
        loadChildren: () =>
          import('./components/catalogos/clients/clients.module').then((m) => m.ClientsModule),
        canLoad: [GeneralRouteGuard],
        canActivate: [GeneralRouteGuard],
        title: ROUTE_TITLES.CLIENTS,
      },
      {
        path: `${appRoutes.catalogs.catalogs}/${appRoutes.catalogs.providers.providers}`,
        loadChildren: () =>
          import('./components/catalogos/providers/providers.module').then(
            (m) => m.ProvidersModule,
          ),
        canLoad: [GeneralRouteGuard],
        canActivate: [GeneralRouteGuard],
        title: ROUTE_TITLES.PROVIDERS,
      },
      {
        path: `${appRoutes.catalogs.catalogs}/${appRoutes.catalogs.products.products}`,
        loadChildren: () =>
          import('./components/catalogos/products/products.module').then((m) => m.ProductsModule),
        canLoad: [GeneralRouteGuard],
        canActivate: [GeneralRouteGuard],
        title: ROUTE_TITLES.PRODUCTS,
      },
      {
        path: `${appRoutes.catalogs.catalogs}/${appRoutes.catalogs.brands.brands}`,
        loadChildren: () =>
          import('./components/catalogos/brands/brands.module').then((m) => m.BrandsModule),
        canLoad: [GeneralRouteGuard],
        canActivate: [GeneralRouteGuard],
        title: ROUTE_TITLES.BRANDS,
      },
      {
        path: `${appRoutes.catalogs.catalogs}/${appRoutes.catalogs.customsAgents.customsAgents}`,
        loadChildren: () =>
          import('./components/catalogos/customs-agents/customs-agents.module').then(
            (m) => m.CustomsAgentsModule,
          ),
        canLoad: [GeneralRouteGuard],
        canActivate: [GeneralRouteGuard],
        title: ROUTE_TITLES.CUSTOMS_AGENTS,
      },
      {
        path: appRoutes.reports.reports,
        loadChildren: () =>
          import('@appComponents/reports/reports-home/reports-home.module').then(
            (m) => m.ReportsHomeModule,
          ),
        canLoad: [GeneralRouteGuard],
        canActivate: [GeneralRouteGuard],
      },
      {
        path: appRoutes.construction.configuration,
        loadChildren: () =>
          import('@appComponents/configuration/configuration-home/configuration-home.module').then(
            (m) => m.ConfigurationHomeModule,
          ),
        canLoad: [GeneralRouteGuard],
        canActivate: [GeneralRouteGuard],
        title: ROUTE_TITLES.CONFIGURATION,
      },
      {
        path: appRoutes.notFound,
        redirectTo: appRoutes.protected,
        title: ROUTE_TITLES.NOT_FOUND,
      },
    ],
  },
  {
    path: appRoutes.notFound,
    redirectTo: appRoutes.login,
    title: ROUTE_TITLES.NOT_FOUND,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
